// The flown path on a fixed grid of sim seconds, for a check that must not read
// how many frames the machine drew.
//
// Cruise integrates on the frame's own seconds, so a reader that samples once a
// drawn frame reads as many samples as the machine managed and places a landing
// at whichever frame end followed it. This cuts one row every `spacingS` from
// the instant it is armed, each interpolated between the two frame ends around
// it, and gives a ground contact a row of its own at the instant the step met
// the surface. Instrumentation only: nothing here runs until `arm`, and the
// caller skips `note` while it is not armed.

/** Sim seconds, then x, y, z of each tracked point in heliocentric metres. */
export type PathRow = number[];

export interface PathReading {
  readonly spacingS: number;
  readonly rows: readonly PathRow[];
  /** The widest frame the rows were interpolated across, sim seconds. */
  readonly widestStepS: number;
  /** Frame ends noted since `arm`: what a per-frame reader would have read. */
  readonly frames: number;
  /**
   * Frame ends since `arm` with no row cut at all: a tracked body was missing
   * from the registry, so the chord to the next noted frame spans both.
   */
  readonly skipped: number;
  /** Sim seconds of the first ground contact since `arm`, or null. */
  readonly contactS: number | null;
  /** Why the recording stopped taking rows, or null while it still is. */
  readonly stopped: 'rewind' | 'full' | null;
}

export interface PathRecorder {
  readonly armed: boolean;
  /**
   * Start over at `t` with `values` (the ship first, then each body) as the
   * first row. False, and disarmed, on a spacing or a width it cannot use.
   */
  arm(spacingS: number, t: number, values: readonly number[]): boolean;
  /**
   * One frame end. `contactS` is the sim instant of the latest ground contact
   * and `contactCol` the index in `values` of the body it was on, -1 for one
   * the rows do not carry.
   */
  note(t: number, values: readonly number[], contactS: number, contactCol: number): void;
  /**
   * A frame end the caller could not turn into a row (a tracked body missing
   * from the registry): counted rather than silent, span unadvanced.
   */
  skip(): void;
  read(): PathReading | null;
  disarm(): void;
}

/** Rows kept before the recording stops itself: over half an hour of sim at 1/60 s. */
export const PATH_ROWS_MAX = 120_000;

export function createPathRecorder(): PathRecorder {
  let armed = false;
  let spacingS = 0;
  let originS = 0;
  let nextK = 1;
  let prevT = 0;
  let prev: number[] = [];
  let mid: number[] = [];
  let rows: PathRow[] = [];
  let widestStepS = 0;
  let frames = 0;
  let skipped = 0;
  let contactS: number | null = null;
  let stopped: PathReading['stopped'] = null;

  const push = (t: number, a: readonly number[], b: readonly number[], g: number): void => {
    const row: PathRow = new Array<number>(a.length + 1);
    row[0] = t;
    for (let c = 0; c < a.length; c++) row[c + 1] = a[c]! + (b[c]! - a[c]!) * g;
    rows.push(row);
    if (rows.length >= PATH_ROWS_MAX) stopped = 'full';
  };

  // Grid rows in (t0, t1], on the straight line from `a` at t0 to `b` at t1.
  const fill = (t0: number, a: readonly number[], t1: number, b: readonly number[]): void => {
    for (;;) {
      const tk = originS + nextK * spacingS;
      if (tk > t1 || stopped !== null) return;
      push(tk, a, b, (tk - t0) / (t1 - t0));
      nextK++;
    }
  };

  return {
    get armed() { return armed; },

    arm(s, t, values) {
      armed = false;
      rows = [];
      if (!(s > 0) || !Number.isFinite(s) || !Number.isFinite(t)
        || values.length < 3 || values.length % 3 !== 0) return false;
      spacingS = s;
      originS = t;
      nextK = 1;
      prevT = t;
      prev = [...values];
      mid = new Array<number>(values.length).fill(0);
      widestStepS = 0;
      frames = 0;
      skipped = 0;
      contactS = null;
      stopped = null;
      rows.push([t, ...values]);
      armed = true;
      return true;
    },

    note(t, values, cS, contactCol) {
      if (!armed || stopped !== null || values.length !== prev.length) return;
      if (t < prevT) {
        stopped = 'rewind';
        return;
      }
      const h = t - prevT;
      if (!(h > 0)) {
        for (let c = 0; c < values.length; c++) prev[c] = values[c]!;
        return;
      }
      if (h > widestStepS) widestStepS = h;
      frames++;
      if (cS > prevT && cS <= t) {
        // Every point where it stood at the contact; the ship then rode the
        // body it met to the frame's end, so it met it at the frame end less
        // that body's own travel since.
        const f = (cS - prevT) / h;
        for (let c = 0; c < values.length; c++) mid[c] = prev[c]! + (values[c]! - prev[c]!) * f;
        for (let k = 0; k < 3; k++) {
          mid[k] = contactCol >= 3 && contactCol + 2 < values.length
            ? values[k]! - (values[contactCol + k]! - mid[contactCol + k]!)
            : values[k]!;
        }
        fill(prevT, prev, cS, mid);
        if (stopped === null && rows[rows.length - 1]![0] !== cS) push(cS, mid, mid, 0);
        if (contactS === null) contactS = cS;
        fill(cS, mid, t, values);
      } else {
        fill(prevT, prev, t, values);
      }
      prevT = t;
      for (let c = 0; c < values.length; c++) prev[c] = values[c]!;
    },

    skip() {
      if (!armed) return;
      skipped++;
    },

    read() {
      return armed ? { spacingS, rows, widestStepS, frames, skipped, contactS, stopped } : null;
    },

    disarm() {
      armed = false;
      rows = [];
    },
  };
}
